import { create } from 'zustand';
import type { AuthResponse, MeDto } from '@mafia/core';
import { ApiError, api, onSessionChange, refreshSession, setToken } from '@/lib/api';

type Status = 'loading' | 'guest' | 'authed';

interface AuthState {
  status: Status;
  user: MeDto | null;
  /** Причина принудительного выхода (бан, истёкшая сессия) — показывается на странице входа. */
  notice: string | null;
  init(): Promise<void>;
  signedIn(auth: AuthResponse): void;
  setUser(user: MeDto): void;
  logout(notice?: string): Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  notice: null,

  async init() {
    try {
      const auth = await refreshSession();
      set(auth ? { status: 'authed', user: auth.user } : { status: 'guest', user: null });
    } catch (err) {
      set({ status: 'guest', user: null, notice: banNotice(err) });
    }
  },

  signedIn(auth) {
    setToken(auth.accessToken);
    set({ status: 'authed', user: auth.user, notice: null });
  },

  setUser(user) {
    set({ user });
  },

  async logout(notice) {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setToken(null);
    if (get().status !== 'guest') set({ status: 'guest', user: null, notice: notice ?? null });
  },
}));

onSessionChange((auth) => {
  if (auth) useAuth.setState({ user: auth.user, status: 'authed' });
});

export function banNotice(err: unknown): string | null {
  if (err instanceof ApiError && err.code === 'BANNED') {
    const until = err.body.bannedUntil ? ` до ${new Date(err.body.bannedUntil).toLocaleString('ru-RU')}` : ' бессрочно';
    return `Аккаунт заблокирован${until}.${err.body.banReason ? ` Причина: ${err.body.banReason}` : ''}`;
  }
  return null;
}
